/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
// actions
import { discardAddOnsSettings, addOnsSettingsChanged, createAddOnsSetting, updateAddOnsSetting } from 'state/actions/commentingState/addOnsStatus';
// material
import { CopySnippetField, InlineMessage, sendErrorMessage } from '@insticator/insticator-ui'; // iui-material
import AddOnController from 'material/expandablesection/controllers/addoncontroller/addoncontroller';
import ExpandableSection from 'material/expandablesection/expandablesection';
import SectionCard from 'material/expandablesection/sectioncard/sectioncard';
import SectionGroup from 'material/expandablesection/sectiongroup/sectiongroup';
import SectionGroupDescription from 'material/expandablesection/sectiongroupdescription/sectiongroupdescription'
import CommentsCountLabel from 'pages/dashboard/commenting/addons/commentscountersection/commentscountlabel/commentscountlabel';
// utils
// styles
import injectSheet from 'react-jss';
import styles from 'pages/dashboard/commenting/addons/commentscountersection/commentscountersection_styles';


const CommentsCounterSection = (props) => {

  const commentsCounter = useSelector(({commentingState}) => commentingState.addOnsStatus.addOnsData.commentsCounter);
  const isCommentsCounterSettingCreated = useSelector(({commentingState}) => commentingState.addOnsStatus.isSettingCreated.commentsCounter);
  const commentingUnitConfigurationUUID = useSelector(({commentingState}) => commentingState.configStatus.commentingUnitConfigurationUUID);
  const isCommentsCounterSettingChanged = useSelector(({commentingState})=> commentingState.addOnsStatus.isSettingChanged.commentsCounter);
  const isPageIDEnabled = useSelector(({commentingState}) => commentingState.configStatus.general.isPageIDEnabled);

  const dispatch = useDispatch();

  // component state
  const [isOpened, setIsOpened] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const collapse = () => {
    setIsOpened(false);
    setIsProcessing(false);
  }

  const toggleSwitch = () => {
    const addOnsSetting = {
      path: 'comments-counters', 
      type: 'commentsCounter',
      request: {
        ...commentsCounter,
        zeroCommentsLabel: commentsCounter.zeroCommentsLabel.value,
        singleCommentLabel: commentsCounter.singleCommentLabel.value,
        multipleCommentsLabel: commentsCounter.multipleCommentsLabel.value,
        isCommentsCounterEnabled: !commentsCounter.isCommentsCounterEnabled,
        commentingUnitConfigurationUUID
      }
    };

    if(!isCommentsCounterSettingCreated) {
      delete addOnsSetting.request.uuid;
      dispatch(createAddOnsSetting(addOnsSetting));
    } else {
      dispatch(
        updateAddOnsSetting({
          ...addOnsSetting,
          configurationUUID: commentsCounter.uuid,
        })
      );
    }

    if(commentsCounter.isCommentsCounterEnabled) 
      setIsOpened(false);

    dispatch(
      addOnsSettingsChanged({
        addOnsType: 'commentsCounter', 
        property: 'isCommentsCounterEnabled', 
        value: !commentsCounter.isCommentsCounterEnabled
      })
    );
  }

  const handleCancel = () => {
    dispatch(
      discardAddOnsSettings({
        addOnsType: 'commentsCounter', 
        ignoreProperty: {
          name: 'isCommentsCounterEnabled', 
          value: commentsCounter.isCommentsCounterEnabled
        }
      })
    );
  }

  const handleSave = () => {
    setIsProcessing(true);
    dispatch(
      updateAddOnsSetting({
        path: 'comments-counters', 
        type: 'commentsCounter',
        configurationUUID: commentsCounter.uuid,
        request: {
          ...commentsCounter,
          zeroCommentsLabel: commentsCounter.zeroCommentsLabel.value,
          singleCommentLabel: commentsCounter.singleCommentLabel.value,
          multipleCommentsLabel: commentsCounter.multipleCommentsLabel.value
        },
        showSuccessMessage: true,
        label: 'Comments Counter',
        callback: collapse
      })
    );
  }


  const isSectionValid = () => {
    let isValid = true;
    const { zeroCommentsLabel, singleCommentLabel, multipleCommentsLabel } = commentsCounter;
    isValid = isValid && zeroCommentsLabel.valid && singleCommentLabel.valid && multipleCommentsLabel.valid;
    return isValid;
  }

  const saveCommentsCounter = () => {
    if(!isSectionValid()){
      return dispatch(sendErrorMessage('Cannot save: invalid fields'));
    }
    handleSave();
  }

  const controller = () => (
    <AddOnController
      title={'Comments Counter'}
      image={'illustration-comments-counter.svg'}
      description={`Comments counter allows users to immediately see the number 
      of comments on a particular article. Comments counter can be placed 
      anywhere on the page with a fully customizable design.`}
      isOpened={isOpened}
      setIsOpened={setIsOpened}
      toggle={commentsCounter.isCommentsCounterEnabled}
      toggleClick={toggleSwitch}
    />
  );
  
  return (
    <ExpandableSection
      className={`commentscounter ${props.classes.commentscounter}`}
      controller={controller()}
      isOpened={isOpened}
      setIsOpened={setIsOpened}
      processing={isProcessing}
      handleCancel={handleCancel}
      handleSave={saveCommentsCounter}
      disableSave={!isCommentsCounterSettingChanged}
    >
      <SectionCard>
        <SectionGroup>
            <SectionGroupDescription />
            <CopySnippetField 
                title={'Comments Counter Loading Script'} 
                description={
                  <div>
                    Add the following script to the bottom of the page where you’d like to display the Comments Counter, 
                    right before the closing <strong>{'</body>'}</strong>:
                  </div>
                } 
                codeSnippet={commentsCounter.headerCode} 
            />
        </SectionGroup>

        <SectionGroup>
          <SectionGroupDescription />
            <CopySnippetField 
              title={'Comments Counter Code'} 
              description={<div>Add this suffix (anchor) to links where Comments Counter should appear:</div>} 
              codeSnippet={commentsCounter.bodyCode} 
            />
            <InlineMessage 
              name={'commentscountercode'} 
              variant={'neutral'} 
              icon={'exclamation-triangle'} 
              message={
              <div>
                Make sure you replace <strong>&#123;articleURL&#125;</strong> with the link to your specific page
                { !isPageIDEnabled ? '.' : 
                    <span>, and <strong>&#123;extPageId&#125;</strong> with the proper External Page ID value for the specific page.</span>
                }
              </div>
              } 
            />
        </SectionGroup>
      </SectionCard>

      <SectionCard>
        <SectionGroup>
          <SectionGroupDescription 
            title='Label' 
            description={<div>Customize the <strong>comments counter label</strong> for various scenarios:</div>} 
          />
            <CommentsCountLabel 
              data={commentsCounter}
              onChange={({property, value}) => dispatch(addOnsSettingsChanged({addOnsType: 'commentsCounter', property, value}))}
            />
        </SectionGroup>
      </SectionCard>
    </ExpandableSection>
  );
}

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default injectSheet(styles)(CommentsCounterSection)